import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getUrlValue } from 'utilities/content/linkUrls'
import { generateUniqueTabId } from './helpers'
import initialState from './initialState'
import { NavigationTab, NavigationTabIcon } from './types'

const MAX_TABS = 5
const MIN_TABS = 2

const slice = createSlice({
  name: 'appNavigationBottomNav',
  initialState,
  reducers: {
    addTopEmptyTab: state => {
      const tabs = Object.keys(state.navigation_tabs)

      // Don't add if we're at maximum tabs
      if (tabs.length >= MAX_TABS) return

      // Generate a new unique ID (server will assign permanent ID)
      const newId = generateUniqueTabId(state.navigation_tabs)

      // Create new tab with defaults at position 2
      const newTab: NavigationTab = {
        id: newId,
        name: 'New Tab',
        link: getUrlValue('thanx://rewards'),
        position: 2,
      }

      // Shift existing tabs at position 2 or higher
      tabs.forEach(tabId => {
        const tab = state.navigation_tabs[tabId]
        if (tab.position >= 2) {
          state.navigation_tabs[tabId].position += 1
        }
      })

      state.navigation_tabs[newId] = newTab
      state.hasChanges = true
    },
    addBottomEmptyTab: state => {
      const tabs = Object.keys(state.navigation_tabs)
      const tabsLength = tabs.length

      // Don't add if we're at maximum tabs
      if (tabsLength >= MAX_TABS) return

      // Generate a new unique ID (server will assign permanent ID)
      const newId = generateUniqueTabId(state.navigation_tabs)

      // The last position is always tabsLength, new tab goes there
      const newPosition = tabsLength

      // Push the tab at the last position down by 1
      const lastTabId = tabs.find(tabId => {
        const tab = state.navigation_tabs[tabId]
        return tab.position === tabsLength
      })
      if (lastTabId) {
        state.navigation_tabs[lastTabId].position += 1
      }

      // Create new tab at the previous last position
      const newTab: NavigationTab = {
        id: newId,
        name: 'New Tab',
        link: getUrlValue('thanx://rewards'),
        position: newPosition || 1, // Handle empty state
      }

      state.navigation_tabs[newId] = newTab
      state.hasChanges = true
    },
    updateNavigationTab: (
      state,
      action: PayloadAction<{
        id: string | number
        data: Partial<NavigationTab>
      }>
    ) => {
      const id = String(action.payload.id)
      if (state.navigation_tabs[id]) {
        state.navigation_tabs[id] = {
          ...state.navigation_tabs[id],
          ...action.payload.data,
        }
        state.hasChanges = true
      }
    },
    removeNavigationTab: (state, action: PayloadAction<string | number>) => {
      const tabs = Object.keys(state.navigation_tabs)
      const tabsLength = tabs.length
      const id = String(action.payload)
      const tabToRemove = state.navigation_tabs[id]

      if (!tabToRemove) return // Tab doesn't exist, nothing to do
      if (tabsLength <= MIN_TABS) return // Can't remove if at minimum tabs
      if (tabToRemove.position === 1) return // Can't remove first tab
      if (tabToRemove.position >= tabsLength) return // Can't remove last tab

      const removedPosition = tabToRemove.position

      // Remove the tab
      delete state.navigation_tabs[id]

      // Reorder remaining tabs: shift down all tabs with position > removedPosition
      tabs.forEach(tabId => {
        const tab = state.navigation_tabs[tabId]
        if (tab && tab.position > removedPosition) {
          state.navigation_tabs[tabId].position -= 1
        }
      })

      state.hasChanges = true
    },
    resetChanges: state => {
      state.hasChanges = false
    },
    setChanges: state => {
      state.hasChanges = true
    },
    setSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload
    },
    reset: (state, action: PayloadAction<NavigationTab[]>) => {
      // Clear existing tabs and set new ones
      state.navigation_tabs = {}
      action.payload.forEach((tab: NavigationTab) => {
        if (tab.id) {
          state.navigation_tabs[String(tab.id)] = tab
        }
      })
      state.hasChanges = false
    },
    updateNavigationTabIcon: (
      state,
      action: PayloadAction<{
        tabId: string | number
        iconType: 'default_icon' | 'selected_icon'
        iconData: NavigationTabIcon
      }>
    ) => {
      const { tabId, iconType, iconData } = action.payload
      const id = String(tabId)
      if (state.navigation_tabs[id]) {
        state.navigation_tabs[id][iconType] = iconData
        state.hasChanges = true
      }
    },
    removeNavigationTabIcon: (
      state,
      action: PayloadAction<{
        tabId: string | number
        iconType: 'default_icon' | 'selected_icon'
      }>
    ) => {
      const { tabId, iconType } = action.payload
      const id = String(tabId)
      if (state.navigation_tabs[id]) {
        state.navigation_tabs[id][iconType] = undefined
        state.hasChanges = true
      }
    },

    // Modal actions
    openDeleteModal: (state, action: PayloadAction<string | number>) => {
      state.deleteModal.isOpen = true
      state.deleteModal.tabId = action.payload
    },
    closeDeleteModal: state => {
      state.deleteModal.isOpen = false
      state.deleteModal.tabId = null
    },

    // Move tab up by swapping positions with the tab above
    moveTabUp: (state, action: PayloadAction<string | number>) => {
      const tabs = Object.keys(state.navigation_tabs)
      const tabId = String(action.payload)
      const currentTab = state.navigation_tabs[tabId]

      if (!currentTab) return
      if (currentTab.position <= 2) return // Can't move up from position <= 2 (first tab stays first)

      const currentPosition = currentTab.position
      const targetPosition = currentPosition - 1

      // Find the tab at the target position
      const targetTabId = tabs.find(id => {
        const tab = state.navigation_tabs[id]
        return tab.position === targetPosition
      })

      if (targetTabId) {
        // Swap positions
        state.navigation_tabs[tabId].position = targetPosition
        state.navigation_tabs[targetTabId].position = currentPosition
        state.hasChanges = true
      }
    },

    // Move tab down by swapping positions with the tab below
    moveTabDown: (state, action: PayloadAction<string | number>) => {
      const tabs = Object.keys(state.navigation_tabs)
      const tabsLength = tabs.length
      const tabId = String(action.payload)
      const currentTab = state.navigation_tabs[tabId]

      if (!currentTab) return

      // More tab stays at last position
      if (currentTab.position >= tabsLength - 1) return

      const currentPosition = currentTab.position
      const targetPosition = currentPosition + 1

      // Find the tab at the target position
      const targetTabId = tabs.find(id => {
        const tab = state.navigation_tabs[id]
        return tab.position === targetPosition
      })

      if (targetTabId) {
        // Swap positions
        state.navigation_tabs[tabId].position = targetPosition
        state.navigation_tabs[targetTabId].position = currentPosition
        state.hasChanges = true
      }
    },

    // New action to save draft data to main state
    saveDraft: (state, action: PayloadAction<NavigationTab[]>) => {
      // Clear existing tabs and set new ones from draft
      state.navigation_tabs = {}
      action.payload.forEach((tab: NavigationTab) => {
        if (tab.id) {
          state.navigation_tabs[String(tab.id)] = tab
        }
      })
      state.hasChanges = true
    },
  },
})

export const reducer = slice.reducer
export const {
  addTopEmptyTab,
  addBottomEmptyTab,
  updateNavigationTab,
  removeNavigationTab,
  resetChanges,
  setChanges,
  setSubmitting,
  reset,
  updateNavigationTabIcon,
  removeNavigationTabIcon,
  openDeleteModal,
  closeDeleteModal,
  moveTabUp,
  moveTabDown,
  saveDraft,
} = slice.actions
